/**
 * WeekStrip — ukestripen i datopanelet (#152, mangler i MAPPING.md §3).
 *
 * Sju kolonner, mandag først: ukedag (12 px / 600), dato (16 px) og en
 * streak-prikk. I dag er en fylt sirkel (`bg-on-panel text-on-panel-solid`,
 * 36 px) med `aria-current="date"`; en fullført dag har fylt prikk, i dag en
 * hul ring. Stripen ligger alltid på datopanelet, så fargene er
 * panel-tokenene og like i begge temaer.
 *
 * Prikken er pynt; «i dag» og «fullført» står også som skjult tekst, så
 * fargen er aldri eneste kjennetegn.
 *
 * Kallstedet bestemmer hvilke dager som er fullført (`isDone`), så stripen
 * ikke vet noe om streaks.
 *
 * Kalenderen (#157, steg 7a) bruker to tillegg:
 *   - `dots`: prikker i personfarger i stedet for streak-prikken, én per
 *     person med noe den dagen. `dayHint` gir dagen en skjult tekst («2
 *     hendelser»), så prikkene ikke er eneste kjennetegn;
 *   - `onSelect` + `selected`: dagene blir knapper som velger dagen
 *     (`aria-pressed`). Den valgte dagen er den fylte sirkelen; i dag har en
 *     ring når en annen dag er valgt.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { addDays, isSameDay, startOfWeek } from 'date-fns';
import { STRIPE_BG, type ColorStripeSlot } from './EventColorStripe';

/** Flere personer enn dette på én dag vises ikke som egne prikker. */
const MAX_DOTS = 4;

interface WeekStripProps {
  /** En hvilken som helst dag i uka som skal vises. */
  weekOf: Date;
  today: Date;
  isDone?: (day: Date) => boolean;
  /** Personfargene som har noe den dagen; erstatter streak-prikken. */
  dots?: (day: Date) => readonly ColorStripeSlot[];
  /** Skjult tekst etter datoen, f.eks. antall hendelser. */
  dayHint?: (day: Date) => string | undefined;
  /** Valgt dag når dagene er knapper (`onSelect`). */
  selected?: Date;
  onSelect?: (day: Date) => void;
  /** Tilgjengelig navn på lista; standard «Streak per dag». */
  label?: string;
  className?: string;
}

export function WeekStrip({
  weekOf,
  today,
  isDone,
  dots,
  dayHint,
  selected,
  onSelect,
  label,
  className,
}: WeekStripProps): ReactNode {
  const { t } = useTranslation();
  const monday = startOfWeek(weekOf, { weekStartsOn: 1 });
  const days = Array.from({ length: 7 }, (_unused, i) => addDays(monday, i));

  return (
    <ol
      aria-label={label ?? t('weekStrip.label')}
      data-testid="week-strip"
      className={['m-0 grid list-none grid-cols-7 p-0 text-center text-on-panel', className]
        .filter(Boolean)
        .join(' ')}
    >
      {days.map((day, i) => {
        const isToday = isSameDay(day, today);
        const isSelected = onSelect ? selected !== undefined && isSameDay(day, selected) : false;
        // Fylt sirkel: den valgte dagen når dagene kan velges, ellers i dag.
        const filled = onSelect ? isSelected : isToday;
        const done = isDone?.(day) ?? false;
        const hint = dayHint?.(day);
        const content = (
          <>
            <span className="text-caption font-semibold">
              {t(`recurrence.weekdayShort.${i + 1}`)}
            </span>
            <span
              className={[
                'flex h-9 w-9 items-center justify-center rounded-full text-base tnum',
                filled
                  ? 'bg-on-panel text-on-panel-solid font-extrabold'
                  : isToday
                    ? 'font-extrabold ring-2 ring-inset ring-on-panel'
                    : 'font-bold',
              ].join(' ')}
            >
              {day.getDate()}
            </span>
            {dots ? (
              <span aria-hidden className="flex h-1.5 gap-0.5">
                {dots(day)
                  .slice(0, MAX_DOTS)
                  .map((slot, j) => (
                    <span key={`${slot}-${j}`} className={`block h-1.5 w-1.5 rounded-full ${STRIPE_BG[slot]}`} />
                  ))}
              </span>
            ) : (
              <span
                aria-hidden
                className={[
                  'block h-1.5 w-1.5 rounded-full',
                  done ? 'bg-panel-mark' : isToday ? 'border-2 border-panel-mark' : '',
                ].join(' ')}
              />
            )}
            {isToday ? <span className="sr-only">{t('weekStrip.today')}</span> : null}
            {done ? <span className="sr-only">{t('weekStrip.done')}</span> : null}
            {hint ? <span className="sr-only">{hint}</span> : null}
          </>
        );
        return (
          <li
            key={day.toISOString()}
            data-testid="week-strip-day"
            data-today={isToday ? 'true' : undefined}
            data-done={done ? 'true' : undefined}
            data-selected={isSelected ? 'true' : undefined}
            aria-current={isToday ? 'date' : undefined}
            className="flex flex-col items-center gap-1"
          >
            {onSelect ? (
              <button
                type="button"
                aria-pressed={isSelected}
                onClick={() => onSelect(day)}
                className="flex min-h-11 w-full flex-col items-center gap-1 rounded-md py-1"
              >
                {content}
              </button>
            ) : (
              content
            )}
          </li>
        );
      })}
    </ol>
  );
}
